import { type FormEvent, useEffect, useRef, useState } from "react";

import { ApiError, type ParseMealView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, FormField, H1, Input, MonoMeta, PrimaryButton, TopBar } from "./ui/primitives";

type Props = {
  onParsed: (parsed: ParseMealView) => void;
  onCancel: () => void;
};

type Phase = "scanning" | "looking-up" | "manual" | "name-fallback" | "error";

const SCAN_INTERVAL_MS = 300;

export function MealBarcodeScreen({ onParsed, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<BarcodeDetector | null>(null);
  const scanTimerRef = useRef<number | null>(null);

  const supportsNative = typeof window !== "undefined" && "BarcodeDetector" in window;

  const [phase, setPhase] = useState<Phase>(supportsNative ? "scanning" : "manual");
  const [manualEan, setManualEan] = useState("");
  const [manualName, setManualName] = useState("");
  const [portion, setPortion] = useState("100");
  const [error, setError] = useState<string | null>(null);
  const [nameSubmitting, setNameSubmitting] = useState(false);

  function teardownScanner() {
    if (scanTimerRef.current !== null) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  async function lookup(ean: string, portionGrams: number) {
    setPhase("looking-up");
    setError(null);
    try {
      const parsed = await api.parseMealBarcode(ean, portionGrams);
      if (parsed.items.length === 0) {
        // Defensive — backend now 404s instead of returning empty items.
        teardownScanner();
        setPhase("name-fallback");
        return;
      }
      teardownScanner();
      onParsed(parsed);
    } catch (err) {
      // 404 = no source (OFF, FatSecret) knows this code. Offer the
      // name-entry fallback instead of dead-ending the user.
      if (err instanceof ApiError && err.status === 404) {
        teardownScanner();
        setError(null);
        setPhase("name-fallback");
        return;
      }
      const msg = err instanceof Error ? err.message : t.barcodeGenericError;
      setError(msg);
      setPhase(supportsNative ? "scanning" : "manual");
    }
  }

  async function handleNameSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const name = manualName.trim();
    if (name.length < 2) {
      setError(t.barcodeEnterName);
      return;
    }
    setNameSubmitting(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(name);
      if (parsed.items.length === 0) {
        setError(t.barcodeEstimateFailed);
        return;
      }
      onParsed(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.barcodeGenericError);
    } finally {
      setNameSubmitting(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: lookup/portion captured by closure, scanner only restarts when phase changes.
  useEffect(() => {
    if (phase !== "scanning") return;
    let cancelled = false;

    async function start() {
      try {
        if (!detectorRef.current && supportsNative) {
          detectorRef.current = new BarcodeDetector({
            formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"],
          });
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play().catch(() => null);

        scanTimerRef.current = window.setInterval(async () => {
          const detector = detectorRef.current;
          const v = videoRef.current;
          if (!detector || !v || v.readyState < 2) return;
          try {
            const results = await detector.detect(v);
            const first = results[0];
            if (!first) return;
            const ean = first.rawValue.replace(/\D/g, "");
            if (ean.length < 8) return;
            if (scanTimerRef.current !== null) {
              window.clearInterval(scanTimerRef.current);
              scanTimerRef.current = null;
            }
            const portionGrams = Math.max(1, Number(portion) || 100);
            lookup(ean, portionGrams);
          } catch {
            // detector throws on partial frames; keep scanning.
          }
        }, SCAN_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error && err.name === "NotAllowedError"
            ? t.barcodeCameraDenied
            : t.barcodeCameraError,
        );
        setPhase("manual");
      }
    }
    start();
    return () => {
      cancelled = true;
      teardownScanner();
    };
  }, [phase, supportsNative]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup only.
  useEffect(() => teardownScanner, []);

  function handleManualSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ean = manualEan.replace(/\D/g, "");
    if (ean.length < 8 || ean.length > 14) {
      setError(t.barcodeEanInvalid);
      return;
    }
    const portionGrams = Math.max(1, Number(portion) || 100);
    lookup(ean, portionGrams);
  }

  const topBarTitle =
    phase === "looking-up"
      ? t.barcodeBackTitleLooking
      : phase === "manual"
        ? t.barcodeBackTitleManual
        : phase === "name-fallback"
          ? t.barcodeBackTitleName
          : t.barcodeBackTitleScanning;

  return (
    <ScreenShell hideHeader>
      <TopBar
        back={t.barcodeBack}
        title={topBarTitle}
        onBack={() => {
          teardownScanner();
          onCancel();
        }}
      />

      <div style={{ marginTop: 18 }}>
        <H1 accent=".">{t.barcodeH1}</H1>
        <p className="text-[14px] leading-snug" style={{ marginTop: 10, color: "var(--ink-2)" }}>
          {t.barcodeLead}
        </p>
      </div>

      {/* Scanner viewfinder — only mounted while we're scanning. */}
      {phase === "scanning" ? (
        <div
          className="relative"
          style={{
            marginTop: 24,
            height: 280,
            borderRadius: 18,
            background: "var(--ink)",
            overflow: "hidden",
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.65 }}
          >
            <track kind="captions" />
          </video>

          {/* Horizontal scan line — pure CSS, animated up/down. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 24,
              right: 24,
              top: "50%",
              height: 2,
              background: "var(--accent)",
              boxShadow: "0 0 12px var(--accent)",
            }}
          />

          {/* Four corner brackets. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 96,
              left: 20,
              width: 24,
              height: 24,
              borderTop: "2px solid var(--accent)",
              borderLeft: "2px solid var(--accent)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 96,
              right: 20,
              width: 24,
              height: 24,
              borderTop: "2px solid var(--accent)",
              borderRight: "2px solid var(--accent)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: 96,
              left: 20,
              width: 24,
              height: 24,
              borderBottom: "2px solid var(--accent)",
              borderLeft: "2px solid var(--accent)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: 96,
              right: 20,
              width: 24,
              height: 24,
              borderBottom: "2px solid var(--accent)",
              borderRight: "2px solid var(--accent)",
            }}
          />

          <div
            style={{
              position: "absolute",
              top: 20,
              left: 20,
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--accent)",
              letterSpacing: "0.1em",
            }}
          >
            {t.barcodeStatusScanning}
          </div>
        </div>
      ) : null}

      {/* Manual EAN entry. */}
      {phase === "manual" ? (
        <form
          onSubmit={handleManualSubmit}
          className="flex flex-col gap-3"
          style={{ marginTop: 24 }}
        >
          <FormField label={t.barcodeEanLabel}>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{8,14}"
              required
              value={manualEan}
              onChange={(e) => setManualEan(e.target.value)}
              placeholder={t.barcodeEanPlaceholder}
              style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.05em" }}
            />
          </FormField>
          <FormField label={t.barcodePortionLabel} hint={t.barcodePortionHint}>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={5000}
              step={10}
              value={portion}
              onChange={(e) => setPortion(e.target.value)}
            />
          </FormField>
          <PrimaryButton type="submit">{t.barcodeCheck}</PrimaryButton>
        </form>
      ) : null}

      {/* Name fallback — no source knew the barcode, estimate from a name. */}
      {phase === "name-fallback" ? (
        <form onSubmit={handleNameSubmit} className="flex flex-col gap-3" style={{ marginTop: 24 }}>
          <p
            className="text-[13px] px-3 py-2 rounded-md"
            style={{ background: "var(--warn-bg)", color: "var(--warn-fg)" }}
          >
            {t.barcodeNameFallbackNote}
          </p>
          <FormField label={t.barcodeNameLabel} hint={t.barcodeNameHint}>
            <Input
              type="text"
              required
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder={t.barcodeNamePlaceholder}
            />
          </FormField>
          <PrimaryButton type="submit" disabled={nameSubmitting}>
            {nameSubmitting ? t.barcodeEstimating : t.barcodeEstimate}
          </PrimaryButton>
        </form>
      ) : null}

      {/* Portion + status strip when scanning. */}
      {phase === "scanning" ? (
        <div
          className="flex items-center justify-between"
          style={{
            marginTop: 18,
            padding: "14px 16px",
            borderRadius: 10,
            background: "var(--surface)",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <div className="flex-1 min-w-0">
            <MonoMeta>{t.barcodePortionMeta}</MonoMeta>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={5000}
              step={10}
              value={portion}
              onChange={(e) => setPortion(e.target.value)}
              aria-label={t.barcodePortionAria}
              className="w-full bg-transparent outline-none tabular"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 800,
                color: "var(--ink)",
                letterSpacing: "-0.03em",
                marginTop: 2,
                border: "none",
                padding: 0,
              }}
            />
          </div>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--muted)",
              letterSpacing: "0.1em",
            }}
          >
            G
          </span>
        </div>
      ) : null}

      {phase === "looking-up" ? (
        <div
          className="text-center"
          style={{
            marginTop: 24,
            padding: "32px 16px",
            borderRadius: 14,
            background: "var(--surface)",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <Eyebrow>{t.barcodeLookingEyebrow}</Eyebrow>
          <p
            className="tabular"
            style={{
              marginTop: 8,
              fontFamily: "var(--font-display)",
              fontSize: 26,
              fontWeight: 800,
              color: "var(--ink)",
              letterSpacing: "-0.03em",
            }}
          >
            {t.barcodeLookingSource}
          </p>
        </div>
      ) : null}

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 16 }}
        >
          {error}
        </p>
      ) : null}

      {/* Mode-toggle / secondary CTA at the bottom. */}
      {phase === "scanning" ? (
        <button
          type="button"
          onClick={() => setPhase("manual")}
          className="w-full uppercase transition-colors"
          style={{
            marginTop: 18,
            padding: "15px 18px",
            borderRadius: 12,
            background: "transparent",
            color: "var(--ink)",
            boxShadow: "inset 0 0 0 1px var(--nav-border)",
            border: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
          }}
        >
          {t.barcodeManualToggle}
        </button>
      ) : null}

      {phase === "manual" && supportsNative ? (
        <button
          type="button"
          onClick={() => setPhase("scanning")}
          className="w-full uppercase transition-colors"
          style={{
            marginTop: 12,
            padding: "15px 18px",
            borderRadius: 12,
            background: "transparent",
            color: "var(--ink)",
            boxShadow: "inset 0 0 0 1px var(--nav-border)",
            border: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
          }}
        >
          {t.barcodeScanToggle}
        </button>
      ) : null}

      {phase === "scanning" && !supportsNative ? (
        <p
          className="text-[12px]"
          style={{
            marginTop: 16,
            color: "var(--muted)",
          }}
        >
          {t.barcodeUnsupportedHint}
        </p>
      ) : null}
    </ScreenShell>
  );
}
